import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import RefreshIcon from "@mui/icons-material/Refresh";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import { formatDistanceToNow } from "date-fns";
import { COLORS } from "../../assets/theme";
import FeatureChips from "../../components/superadmin/FeatureChips";
import {
  SA,
  SaPageHeader,
  saCardSx,
  saTableBodySx,
  saTableHeadSx,
  saToggleSx,
} from "../../components/superadmin/saUi";
import {
  PlatformChip,
  SubscriptionStatusChip,
  describeSubscriptionEnd,
} from "../../components/superadmin/SubscriptionChips";
import {
  getFeatureKeys,
  listOrganizations,
  OrganizationRow,
  OrganizationsPage,
  PLATFORM_KEYS,
  PLATFORM_LABELS,
  PlatformKey,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_STATUS_LABELS,
  SubscriptionStatus,
} from "../../services/superadminService";

const PAGE_SIZES = [10, 25, 50, 100];

const relativeOrNever = (value: string | null) => {
  if (!value) return "Never";
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true });
  } catch {
    return "—";
  }
};

/** A dormant account: never booked, or nothing in the last 30 days. */
const isDormant = (org: OrganizationRow) => {
  if (!org.usage?.lastBookingAt) return true;
  const last = new Date(org.usage.lastBookingAt).getTime();
  return Date.now() - last > 30 * 24 * 60 * 60 * 1000;
};

/** "Joined today", "Joined 42 days ago". */
const joinedLabel = (ageDays: number) =>
  ageDays === 0
    ? "Joined today"
    : `Joined ${ageDays} day${ageDays === 1 ? "" : "s"} ago`;

const Organizations: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParams] = useSearchParams();
  const linkedPlatform = searchParams.get("platform") as PlatformKey | null;
  const [platform, setPlatform] = useState<PlatformKey | "ALL">(
    linkedPlatform && PLATFORM_KEYS.includes(linkedPlatform) ? linkedPlatform : "ALL"
  );
  const [status, setStatus] = useState<SubscriptionStatus | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [hideDeleted, setHideDeleted] = useState(true);
  const [data, setData] = useState<OrganizationsPage | null>(null);
  const [featureKeys, setFeatureKeys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Read the canonical key list once so a fourth feature needs no release.
    getFeatureKeys()
      .then(setFeatureKeys)
      .catch(() => setFeatureKeys([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listOrganizations({
        page,
        pageSize,
        search: debouncedSearch || undefined,
        platform: platform === "ALL" ? undefined : platform,
        subscription: status ?? undefined,
        // Server-side, so the total and the page count match what is shown.
        hideDeleted,
      });
      setData(result);
    } catch {
      // The interceptor already surfaced the message.
    }
    setLoading(false);
  }, [page, pageSize, debouncedSearch, platform, status, hideDeleted]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = data?.organizations || [];
  const windowDays = data?.windowDays ?? 30;
  const totalPages = data?.totalPages ?? 1;

  return (
    <Box>
      <SaPageHeader
        title="Organizations"
        subtitle={`${data ? `${data.total} total` : "Loading…"} · activity counted over the last ${windowDays} days`}
        icon={<ApartmentOutlinedIcon />}
        actions={
          <ToggleButtonGroup
            size="small"
            exclusive
            value={platform}
            onChange={(_, value) => {
              if (!value) return;
              setPlatform(value);
              setPage(1);
            }}
            sx={saToggleSx}
          >
            <ToggleButton value="ALL">All</ToggleButton>
            {PLATFORM_KEYS.map((key) => (
              <ToggleButton key={key} value={key}>
                {PLATFORM_LABELS[key]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        }
      />

      <Paper elevation={0} sx={{ ...saCardSx, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            flexWrap: "wrap",
            borderBottom: `1px solid ${SA.divider}`,
          }}
        >
          <TextField
            size="small"
            placeholder="Search organizations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {SUBSCRIPTION_STATUSES.map((key) => (
              <Chip
                key={key}
                label={SUBSCRIPTION_STATUS_LABELS[key]}
                size="small"
                clickable
                color={status === key ? "primary" : "default"}
                variant={status === key ? "filled" : "outlined"}
                onClick={() => {
                  setStatus((prev) => (prev === key ? null : key));
                  setPage(1);
                }}
                sx={{ fontWeight: 600, height: 30 }}
              />
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={hideDeleted}
                onChange={(e) => {
                  setHideDeleted(e.target.checked);
                  setPage(1);
                }}
              />
            }
            label={
              <Typography sx={{ fontSize: "0.88rem" }}>Hide deleted</Typography>
            }
          />
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Refresh">
            <IconButton onClick={load} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {isMobile ? (
          <Box>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={26} />
              </Box>
            )}
            {!loading && rows.length === 0 && (
              <Typography
                sx={{ color: SA.textMuted, textAlign: "center", py: 6 }}
              >
                No organizations match these filters.
              </Typography>
            )}
            {!loading &&
              rows.map((org) => (
                <Box
                  key={org.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/superadmin/organizations/${org.id}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      navigate(`/superadmin/organizations/${org.id}`);
                  }}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: `1px solid ${SA.divider}`,
                    cursor: "pointer",
                    "&:active": { bgcolor: SA.surfaceMuted },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 1,
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          color: org.isDeleted ? "#A32B22" : undefined,
                        }}
                      >
                        {org.isDeleted ? `${org.name} (Deleted)` : org.name}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.76rem", color: SA.textMuted }}
                      >
                        {joinedLabel(org.ageDays)} · {org.usage?.bookingsInWindow ?? 0}{" "}
                        bookings / {windowDays}d
                      </Typography>
                    </Box>
                    <PlatformChip platform={org.platform} businessType={org.businessType} />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <SubscriptionStatusChip summary={org.subscription} />
                    <Typography sx={{ fontSize: "0.76rem", color: SA.textMuted }}>
                      {describeSubscriptionEnd(org.subscription)}
                    </Typography>
                  </Box>
                </Box>
              ))}
          </Box>
        ) : (
        <TableContainer>
          <Table size="small" sx={{ minWidth: 1000 }}>
            <TableHead sx={saTableHeadSx}>
              <TableRow>
                <TableCell>Organization</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell align="right">Activity</TableCell>
                <TableCell>Last booking</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Features</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={saTableBodySx}>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography sx={{ color: SA.textMuted }}>
                      No organizations match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                rows.map((org) => (
                  <TableRow
                    key={org.id}
                    hover
                    onClick={() => navigate(`/superadmin/organizations/${org.id}`)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.92rem",
                          color: org.isDeleted ? "#A32B22" : undefined,
                        }}
                      >
                        {org.name}
                        {org.isDeleted && (
                          <Typography
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              color: COLORS.RED,
                            }}
                          >
                            DELETED
                          </Typography>
                        )}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          mt: 0.25,
                        }}
                      >
                        <PlatformChip platform={org.platform} businessType={org.businessType} />
                        <Typography
                          sx={{
                            fontSize: "0.76rem",
                            color: SA.textMuted,
                          }}
                        >
                          +{org.country} · {joinedLabel(org.ageDays)}
                        </Typography>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <SubscriptionStatusChip summary={org.subscription} />
                      <Typography
                        sx={{
                          fontSize: "0.74rem",
                          color: SA.textMuted,
                          mt: 0.25,
                        }}
                      >
                        {org.subscription.plan?.name
                          ? `${org.subscription.plan.name} · `
                          : ""}
                        {describeSubscriptionEnd(org.subscription)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>
                        {org.usage?.bookingsInWindow ?? 0}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.74rem",
                          color: SA.textMuted,
                        }}
                      >
                        {org.usage?.bookingsTotal ?? 0} all time
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: isDormant(org)
                            ? COLORS.ORANGE
                            : COLORS.BlackText,
                          fontWeight: isDormant(org) ? 600 : 400,
                        }}
                      >
                        {relativeOrNever(org.usage?.lastBookingAt ?? null)}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography sx={{ fontSize: "0.85rem" }}>
                        {org.usage?.clients ?? 0} clients
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.74rem",
                          color: SA.textMuted,
                        }}
                      >
                        {org.usage?.staff ?? 0} staff ·{" "}
                        {org.usage?.businesses ?? 0} businesses
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          flexWrap: "wrap",
                        }}
                      >
                        <FeatureChips features={org.features} keys={featureKeys} />
                        {org.publicBrowseEnabled && (
                          <Tooltip title="Public catalogue open to visitors">
                            <Chip
                              size="small"
                              label="Public"
                              sx={{
                                fontSize: "0.72rem",
                                height: 22,
                                fontWeight: 600,
                                bgcolor: "#e6eef8",
                                color: "#2a5a8c",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 2,
            p: 1.5,
            flexWrap: "wrap",
            borderTop: `1px solid ${SA.divider}`,
          }}
        >
          <Typography sx={{ fontSize: "0.82rem", color: SA.textMuted }}>
            Rows per page
          </Typography>
          <Select
            size="small"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            sx={{ height: 32 }}
          >
            {PAGE_SIZES.map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ fontSize: "0.82rem" }}>
            Page {data?.page ?? page} of {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={page <= 1 || loading}
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
          >
            <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
          </IconButton>
          <IconButton
            size="small"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((prev) => prev + 1)}
          >
            <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Paper>
    </Box>
  );
};

export default Organizations;
